import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchAuditorDashboard } from "../api/auditorApi";
import type { CompanyBillingInvoice } from "../api/types";
import { getAuthToken, getAuthUser } from "../auth";
import { auditorDashboardData, type AuditorSubmittedInvoice } from "../data/auditorDashboard";

function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "na";
}

function mapSubmittedRow(row: CompanyBillingInvoice): AuditorSubmittedInvoice {
  const created = row.created_at ? new Date(row.created_at) : null;
  const submittedDate = created
    ? created.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";
  const delayDays = created
    ? Math.max(0, Math.floor((Date.now() - created.getTime()) / (1000 * 60 * 60 * 24)))
    : 0;

  return {
    id: `auditor-inv-${row.id}`,
    displayId: row.id,
    hospital: row.hospital,
    hospitalKey: slugify(row.hospital),
    company: row.company || row.label,
    companyKey: slugify(row.company || row.label),
    amount: row.amount_usd,
    submittedDate,
    delayDays,
    priority: delayDays >= 14 ? "urgent" : "normal",
  };
}

export function useAuditorDashboard() {
  const [data, setData] = useState(auditorDashboardData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const load = useCallback(async () => {
    if (!getAuthToken()) {
      setUsingApi(false);
      setData(auditorDashboardData);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const api = await fetchAuditorDashboard();
      const user = getAuthUser();
      const name = api.auditor_name || user?.name || auditorDashboardData.header.roleBadge;
      const stats = api.stats;

      setData({
        ...auditorDashboardData,
        header: {
          invoicesToAudit: api.header.invoices_to_audit,
          pendingRequests: api.header.pending_requests ?? stats.pending_proformas ?? 0,
          roleBadge: api.header.role_badge || name,
          displayDate: api.header.display_date,
        },
        indicators: auditorDashboardData.indicators.map((indicator) => {
          const value =
            indicator.id === "invoices"
              ? stats.total_invoices
              : indicator.id === "submitted"
                ? stats.submitted_invoices
                : indicator.id === "review"
                  ? stats.under_review_invoices
                  : indicator.id === "approved"
                    ? stats.approved_invoices
                    : indicator.id === "paid"
                      ? stats.paid_invoices
                      : indicator.id === "rejected"
                        ? stats.rejected_invoices
                        : indicator.id === "validation"
                          ? stats.pending_proformas ?? 0
                          : indicator.value;

          return { ...indicator, value };
        }),
        submittedInvoices: api.recent_submitted.map(mapSubmittedRow),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible de charger le dashboard.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const hospitalOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of data.submittedInvoices) {
      map.set(row.hospitalKey, row.hospital);
    }
    return [...map.entries()].map(([value, label]) => ({ value, label }));
  }, [data.submittedInvoices]);

  const companyOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of data.submittedInvoices) {
      map.set(row.companyKey, row.company);
    }
    return [...map.entries()].map(([value, label]) => ({ value, label }));
  }, [data.submittedInvoices]);

  return {
    data,
    loading,
    error,
    usingApi,
    hospitalOptions,
    companyOptions,
    reload: load,
  };
}
